// ===================== Achievements =====================
// Persistent, one-time unlocks. Some fire live during a run (the LIVE RULES at
// the bottom of this file, which Game._checkLiveAchievements walks each frame);
// the rest are evaluated from the run summary at the end.
// Each awards a few meta coins on first unlock.
import { defineLiveAchievement } from '../core/registries.js';

// `secret: true` hides an achievement's description until it unlocks — reserved
// for the handful of genuine discoveries (Alphas, Ascendant elements). Every
// other locked row shows its goal: a fresh player staring at 36 rows of "???"
// has nothing to chase.
export const ACHIEVEMENTS = [
  { id: 'first_blood', name: 'First Blood', icon: '🩸', desc: 'Defeat your first enemy.', coins: 5 },
  { id: 'boss_slayer', name: 'Boss Slayer', icon: '👑', desc: 'Defeat a boss.', coins: 10 },
  { id: 'centurion', name: 'Centurion', icon: '⚔', desc: 'Reach 100 kills in one run.', coins: 10 },
  { id: 'slayer', name: 'Slayer', icon: '💀', desc: 'Reach 1,000 kills in one run.', coins: 25 },
  { id: 'annihilator', name: 'Annihilator', icon: '☠', desc: 'Reach 5,000 kills in one run.', coins: 50 },
  { id: 'legion', name: 'Legion Breaker', icon: '🗡', desc: 'Defeat 10,000 enemies all-time.', coins: 40 },
  { id: 'survive5', name: 'Made It Five', icon: '⏱', desc: 'Survive 5 minutes.', coins: 10 },
  { id: 'survive10', name: 'Endurance', icon: '⏳', desc: 'Survive 10 minutes.', coins: 20 },
  { id: 'combo100', name: 'Unstoppable', icon: '🔗', desc: 'Hit a 100 kill-streak combo.', coins: 15 },
  { id: 'evolved', name: 'Fusion', icon: '✦', desc: 'Evolve a weapon.', coins: 15 },
  { id: 'arsenal', name: 'Full Arsenal', icon: '🎖', desc: 'Fill all 6 weapon slots.', coins: 20 },
  { id: 'overkill', name: 'Overkill', icon: '✖', desc: 'Reach ×5 Multiplication.', coins: 20 },
  { id: 'elementalist', name: 'Elementalist', icon: '◆', desc: 'Master an element (level 8).', coins: 20 },
  { id: 'element_ascend', name: 'Apotheosis', icon: '★', desc: 'Transform a mastered element into its Ascendant form.', coins: 30, secret: true },
  { id: 'element_ascend_all', name: 'Hexarch', icon: '✶', desc: 'Ascend all six elements.', coins: 150, secret: true },
  { id: 'maxed', name: 'Perfected', icon: '🌟', desc: 'Fill all 6 weapon slots and finish every one — maxed out or evolved.', coins: 75 },
  { id: 'clear_meadow', name: 'Meadow Taken', icon: '🌿', desc: "Storm The Maw's Hollow and claim the Meadow.", coins: 20 },
  { id: 'clear_cavern', name: 'Cavern Taken', icon: '💎', desc: "Storm the Warden's Deep and claim the Crystal Cavern.", coins: 30 },
  { id: 'clear_wastes', name: 'Wastes Taken', icon: '🔥', desc: 'Storm the Pyreclast Throne and claim the Ashen Wastes.', coins: 40 },
  { id: 'clear_void', name: 'Void Taken', icon: '🌀', desc: 'Storm the Devouring Silence and claim the Void.', coins: 60 },
  { id: 'pathfinder', name: 'Pathfinder', icon: '⇢', desc: 'Force a Crossing into a new region.', coins: 25 },
  { id: 'warlord', name: 'Warlord of the Fanglands', icon: '🗺', desc: 'Hold every hex on the world map at once.', coins: 200 },
  { id: 'reclaimer', name: 'Hold the Line', icon: '🛡', desc: 'Win back a hex the horde had retaken.', coins: 30 },
  { id: 'ordeal', name: 'Ordeal', icon: '☠', desc: 'Clear a hex at Threat V.', coins: 60 },
  { id: 'nightmare', name: 'Nightmare', icon: '💀', desc: 'Clear a hex at Threat X — the deepest tier there is.', coins: 250 },
  // Finale bosses — one per region, killed rather than merely outlasted. The
  // Reaper is the hard one on purpose; the other three are a strong build's
  // reward for having something left when the clock runs out.
  { id: 'reaper_bane', name: "Reaper's Bane", icon: '⚰', desc: 'Defeat the Reaper.', coins: 100 },
  { id: 'regent_bane', name: 'Shatterer', icon: '◆', desc: 'Defeat the Shard Regent, finale of the Crystal Cavern.', coins: 60 },
  { id: 'tyrant_bane', name: 'Ashfall', icon: '▲', desc: 'Defeat the Ash Tyrant, finale of the Ashen Wastes.', coins: 60 },
  { id: 'archon_bane', name: 'Silence the Silence', icon: '◇', desc: 'Defeat the Null Archon, finale of the Void.', coins: 60 },
  { id: 'gauntlet_first', name: 'Weekslayer', icon: '⚔', desc: 'Survive a Weekly Gauntlet.', coins: 50 },
  { id: 'gauntlet_five', name: 'Gauntlet Regular', icon: '🏟', desc: 'Survive five Weekly Gauntlets.', coins: 150 },
  { id: 'rich', name: 'Hoarder', icon: '◈', desc: 'Bank 500 coins.', coins: 0 },
  { id: 'superboss', name: 'Alpha Hunter', icon: '🐺', desc: 'Slay an Alpha super boss.', coins: 50, secret: true },
  { id: 'ultra', name: 'Ultraweapon', icon: '⋔', desc: 'Claim an ultraweapon from an Alpha.', coins: 25, secret: true },
  { id: 'ultra_all', name: 'Apex Arsenal', icon: '👑', desc: 'Wield all three ultraweapons.', coins: 100, secret: true },
  { id: 'ascension', name: 'Ascended', icon: '☄', desc: 'Reset the Fang Tree at its apex and transcend.', coins: 0 },
];

export const ACH_BY_ID = Object.fromEntries(ACHIEVEMENTS.map((a) => [a.id, a]));

// ------------------------------------------------------------ the hunt board
// The next few goals worth chasing, shown as the ⚔ HUNTS strip on the world
// map. "Next" means the cheapest unearned coin bounty: each bounty was priced
// by difficulty when the achievement was authored, so the smallest purse is
// the nearest mark (ties keep the list's hand-authored order — sort is
// stable). Two exclusions, both deliberate:
//   · secrets are discoveries, not chases — putting one on the board would
//     spoil the reveal the flag exists to protect;
//   · zero-bounty rows (Hoarder, Ascended) have no purse to vend, and a hunt
//     board that posts unpaid work is a worse promise than a shorter list.
// Callers pass their own isAchieved so this module stays save-agnostic and
// imports cleanly into plain Node for modelling and tests.
export function nextHunts(isAchieved, n = 3) {
  return ACHIEVEMENTS
    .filter((a) => !a.secret && a.coins > 0 && !isAchieved(a.id))
    .sort((x, y) => x.coins - y.coins)
    .slice(0, n);
}

// ---------------------------------------------------------------- live rules
// Predicates over the running game, evaluated every frame while playing.
// unlockAch is idempotent, so a rule that stays true simply keeps re-firing
// into an already-unlocked slot. Keep them allocation-free and cheap.

// PERFECTED — the arsenal with nothing left to take. A weapon is FINISHED when
// no card can improve it again: an evolved form, or a base weapon at max level.
// Ultraweapons are Alpha loot rather than a build choice, so they are excluded
// from both the count and the test — the same rule the Ascendant card already
// uses when it asks for a mastered weapon. Evolving therefore never un-earns
// progress: a maxed base and the evolution it becomes both count as finished.
const PERFECT_WEAPONS = 6;
function finishedWeapons(p) {
  let n = 0, total = 0;
  for (const w of p.weapons) {
    if (w.def.ultra) continue;
    total++;
    if (w.def.evolution || w.level >= w.def.maxLevel) n++;
  }
  return { n, total };
}

defineLiveAchievement('maxed', (g) => {
  const { n, total } = finishedWeapons(g.player);
  return total >= PERFECT_WEAPONS && n >= PERFECT_WEAPONS;
});

// OVERKILL fires at ×5 and that number should not be softened to make the
// achievement easier. Two modelled reasons:
//   · ×5.00 is exactly where the storm stops growing. Every weapon's count is
//     capped at base+12, and the last base weapon to saturate is the base-3
//     one (Arc Bolt, Razor Ring) at 1 + 12/3 = ×5.00. A maxed five-weapon
//     loadout puts 85 projectiles up at ×4, 89 at ×4.5, and 91 at ×5 — and the
//     same 91 at ×10 and ×20.
//   · The reachable multipliers are a sparse lattice (the golden ✖ product ×
//     1.25^cards), and the two values immediately below 5.00 are ×4.88 = 4
//     pickups + 3 cards, a SKIRMISH result, and ×4.67 = 8 pickups + 0 cards,
//     which is every completed Lair. So lowering the bar hands the achievement
//     to the short hexes first, and anything at or below ×4.67 makes it
//     automatic on a Lair clear. ×5 is the only value that costs a Lair
//     exactly one Multiplication card and a Skirmish four.
defineLiveAchievement('overkill', (g) => g.player.projMult >= 5);
defineLiveAchievement('arsenal', (g) => g.player.weapons.length >= 6);
defineLiveAchievement('elementalist', (g) => g.player.element !== 'neutral' && g.player.elementLevel >= 8);
defineLiveAchievement('combo100', (g) => g.combo >= 100);
